import { createSlice, createAction, current, PayloadAction } from "@reduxjs/toolkit";
import { has, get, isNil } from "lodash";
import isNumeric from "@/common/lib/isNumeric";

// Trigger action for saga compatibility (saga watches this)
export const fetchOrganizationSets = createAction<any>(
  "organizationSets/trigger"
);

// External action this slice responds to
import { logoutStart } from "@/store/slices/auth";

// Top level:
// {
//    loading: 0, // qty of active requests (0 when none)
//    data: {}    // data object keyed by org ID
// }
//
// The data object will be keyed by org ID, with each entry being an object
// of assessments (or nulls) keyed by set id. Ex:
//
// {
//   10: {                    // (org #10)
//     20: {                  // (assessment #20)
//       loading: true,
//       failed: false,
//       set: { id: 20, name: "Healthy eating assessment", ... }
//     },
//     482: {                  // (assessment #482)
//       loading: 0,
//       failed: false,
//       set: { id: 482, name: "Healthy activities assessment", ... }
//     },
//   }
// }

interface OrganizationSetsState {
  loading?: number;
  data: Record<number, Record<number, any>>;
}

const initialState: OrganizationSetsState = {
  data: {},
};

/**
 * Removes heavy properties from set objects.
 */
const formatSetObj = (s: any) => {
  delete s.responses;
  return s;
};

const organizationSetsSlice = createSlice({
  name: "organizationSets",
  initialState,
  reducers: {
    fetchOrganizationSetsStart(state, action: PayloadAction<any>) {
      const orgId = Number(action.payload.organization_id);

      // Add org entry if not yet present.
      if (!has(state.data, orgId)) {
        state.data[orgId] = {};
      }

      // Add/increment loading flag (top-level, not org-specific).
      if (state.loading === undefined) {
        state.loading = 1;
      } else {
        state.loading++;
      }

      // If request was for a specific set, create or populate the
      // necessary properties to reflect that.
      if (
        has(action.payload, "set_id") &&
        isNumeric(action.payload.set_id)
      ) {
        state.data[orgId][action.payload.set_id] = {
          loading: true,
          failed: false,
          set: null,
        };
      }
    },
    fetchOrganizationSetsSuccess(state, action: PayloadAction<any>) {
      if (!action.payload?.data || !action.payload?.meta) {
        console.error(
          "Missing payload property in fetchOrganizationSetsSuccess.",
          action.payload
        );
        return;
      }

      const orgId = action.payload.meta.organization_id;
      const newOrgProps: Record<number, any> = {};

      // Handle payloads with at least one set.
      if (action.payload.data.length > 0) {
        for (let i = 0; i < action.payload.data.length; i++) {
          newOrgProps[action.payload.data[i].id] = {
            failed: false,
            loading: false,
            set: formatSetObj(action.payload.data[i]),
          };
        }
      }

      let oldOrgProps: any = {};
      if (!isNil(orgId)) {
        oldOrgProps = get(state.data, orgId, null);
      }

      state.loading = (state.loading || 1) - 1;
      state.data[orgId] = {
        ...oldOrgProps,
        ...newOrgProps,
      };
    },
    fetchOrganizationSetsFailure(state, action: PayloadAction<any>) {
      console.error("fetchOrganizationSetsFailure", current(state), action.payload);
      state.loading = (state.loading || 1) - 1;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logoutStart, () => ({ ...initialState }));
  },
});

export const {
  fetchOrganizationSetsStart,
  fetchOrganizationSetsSuccess,
  fetchOrganizationSetsFailure,
} = organizationSetsSlice.actions;

export default organizationSetsSlice.reducer;
